import { httpRequest } from './httpClient';
import { RawReportPayload } from '../types/report';
import {
    ProfilerAnalyzeResponse,
    ProfilerRequestOptions
} from '../types/profilerApi';
import { USE_BACKEND, apiPath } from '../config/env';
import { authManager } from './authManager';
import { asyncDataDiagnosisService, JobResult } from './asyncDataDiagnosisService';

export interface AsyncJobReportResult {
    report: ProfilerAnalyzeResponse;
    translatedReport: ProfilerAnalyzeResponse | null;
    targetLanguage: string | null;
    translationStats: { fields_translated: number } | null;
}

class ReportService {
    // In-memory cache for analysis results (keyed by data object UUID)
    private analysisResultsCache = new Map<string, ProfilerAnalyzeResponse>();
    private readonly CACHE_KEY = 'data-doctor-analysis-cache';

    /**
     * Store analysis results for a data object (both in memory and localStorage)
     */
    storeAnalysisResults(dataObjectUuid: string, analysisResults: ProfilerAnalyzeResponse): void {
        // Store in memory cache
        this.analysisResultsCache.set(dataObjectUuid, analysisResults);

        // Persist to localStorage for page refresh survival
        try {
            const existingCache = this.getStoredCache();
            existingCache[dataObjectUuid] = {
                ...analysisResults,
                _cached_at: new Date().toISOString()
            };
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(existingCache));
        } catch (error) {
            // localStorage persistence failure - non-critical
        }
    }

    /**
     * Retrieve analysis results for a data object - check cache first, then try API
     */
    async getAnalysisResults(dataObjectUuid: string): Promise<ProfilerAnalyzeResponse | null> {
        if (!USE_BACKEND || !dataObjectUuid) {
            return null;
        }

        // First check in-memory cache (analysis results are stored here after data-diagnosis call)
        let result = this.analysisResultsCache.get(dataObjectUuid);

        if (result) {
            return result;
        }

        // Check localStorage cache as fallback
        try {
            const storedCache = this.getStoredCache();
            const storedResult = storedCache[dataObjectUuid];
            if (storedResult) {
                result = storedResult;
                this.analysisResultsCache.set(dataObjectUuid, result);
                return result;
            }
        } catch (error) {
            // localStorage retrieval failure - non-critical
        }

        // Try to fetch from backend as last resort (though this endpoint may not exist)
        try {
            const userEmail = authManager.getUserEmail();
            if (!userEmail) {
                console.warn('[ReportService] No authenticated user email available for API request');
                return null;
            }

            const response = await httpRequest<ProfilerAnalyzeResponse>(
                apiPath(`data-objects/${encodeURIComponent(dataObjectUuid)}/analysis-results?user_email=${encodeURIComponent(userEmail)}`)
            );

            if (response.success && response.data) {
                result = response.data;
                this.storeAnalysisResults(dataObjectUuid, result);
                return result;
            }
        } catch (error) {
            // Backend fetch failure - non-critical, return null
        }

        return null;
    }

    /**
     * Clear analysis results cache (useful for debugging or cache invalidation)
     */
    clearAnalysisCache(): void {
        this.analysisResultsCache.clear();
        try {
            localStorage.removeItem(this.CACHE_KEY);
        } catch (error) {
            // localStorage clear failure - non-critical
        }
    }

    /**
     * Debug method to check cache contents
     */
    debugCacheContents(): void {
        try {
            const storedCache = this.getStoredCache();
            const keys = Object.keys(storedCache);
        } catch (error) {
            // localStorage read failure - non-critical
        }
    }

    /**
     * Get cached analysis results from localStorage
     */
    private getStoredCache(): Record<string, ProfilerAnalyzeResponse & { _cached_at: string }> {
        try {
            const cached = localStorage.getItem(this.CACHE_KEY);
            return cached ? JSON.parse(cached) : {};
        } catch {
            return {};
        }
    }

    /**
     * Get data quality report for a dataset (legacy method for mock data)
     */
    async getReport(datasetId: string): Promise<RawReportPayload> {
        if (!USE_BACKEND) {
            // Dynamic import for mock data to enable code splitting
            const { default: mockReport } = await import('../test/fixtures/report/rawReport.full.json');

            // Simulate network delay in development
            if (import.meta.env.DEV) {
                await new Promise(resolve => setTimeout(resolve, 800));
            }

            return mockReport as RawReportPayload;
        }

        // Backend implementation - fallback to old endpoint
        const response = await httpRequest<RawReportPayload>(apiPath(`data-quality-report/${datasetId}`));
        if (response.success) {
            return response.data;
        } else {
            throw new Error((response as any).error?.message || 'Request failed');
        }
    }

    /**
     * Start async data quality analysis for file upload
     * Returns job ID for polling and eventual report retrieval
     */
    async startAsyncAnalysisFile(
        file: File,
        datasetName?: string,
        dataObjectUuid?: string,
        folderInfo?: { folderId?: number; folderUuid?: string; folderName?: string },
        targetLanguage?: string
    ): Promise<{ jobId: string; dataObjectUuid: string }> {
        if (!USE_BACKEND) {
            // For mock mode, simulate async behavior
            return {
                jobId: `mock-${Date.now()}`,
                dataObjectUuid: dataObjectUuid || `mock-uuid-${Date.now()}`
            };
        }

        const jobResponse = await asyncDataDiagnosisService.startJob({
            file,
            dataset_name: datasetName || file.name,
            data_object_id: dataObjectUuid,
            target_language: targetLanguage && targetLanguage !== 'en' ? targetLanguage : undefined,
            metadata: folderInfo ? {
                folder_id: folderInfo.folderId,
                folder_uuid: folderInfo.folderUuid,
                folder_name: folderInfo.folderName
            } : undefined
        });

        return {
            jobId: jobResponse.job_id,
            dataObjectUuid: dataObjectUuid || ''
        };
    }

    /**
     * Start async Databricks Delta table analysis
     * Returns job ID for polling and eventual report retrieval
     */
    async startAsyncAnalysisTable(
        tableName: string,
        datasetName?: string,
        dataObjectUuid?: string,
        folderInfo?: { folderId?: number; folderUuid?: string; folderName?: string },
        targetLanguage?: string
    ): Promise<{ jobId: string; dataObjectUuid: string }> {
        if (!USE_BACKEND) {
            return {
                jobId: `mock-table-${Date.now()}`,
                dataObjectUuid: dataObjectUuid || `mock-uuid-${Date.now()}`
            };
        }

        const jobResponse = await asyncDataDiagnosisService.startJob({
            table_name: tableName,
            dataset_name: datasetName || tableName.split('.').pop() || tableName,
            data_object_id: dataObjectUuid,
            target_language: targetLanguage && targetLanguage !== 'en' ? targetLanguage : undefined,
            metadata: folderInfo ? {
                folder_id: folderInfo.folderId,
                folder_uuid: folderInfo.folderUuid,
                folder_name: folderInfo.folderName
            } : undefined
        });

        return {
            jobId: jobResponse.job_id,
            dataObjectUuid: dataObjectUuid || ''
        };
    }

    /**
     * Get report from completed async job
     * Returns both English and translated reports when translation was requested
     */
    async getReportFromAsyncJob(jobId: string): Promise<AsyncJobReportResult> {
        if (!USE_BACKEND) {
            // Return mock report for testing
            const mockReport = await this.getReport('mock');
            return {
                report: this.transformMockToProfilerResponse(mockReport),
                translatedReport: null,
                targetLanguage: null,
                translationStats: null
            };
        }

        const jobResult = await asyncDataDiagnosisService.getJobResult(jobId);

        // Transform English result
        const report = this.transformAsyncResultToProfilerResponse(jobResult.result);

        // Transform translated result if available
        const translatedReport = jobResult.translated_result
            ? this.transformAsyncResultToProfilerResponse(jobResult.translated_result)
            : null;

        return {
            report,
            translatedReport,
            targetLanguage: jobResult.target_language,
            translationStats: jobResult.translation_stats
        };
    }

    /**
     * Transform async API result to ProfilerAnalyzeResponse format
     */
    private transformAsyncResultToProfilerResponse(asyncResult: JobResult['result']): ProfilerAnalyzeResponse {
        const problems = asyncResult.reasoning_result?.problematic_columns?.flatMap((col) =>
            col.issues?.map((issue) => ({
                id: `${col.column_name}-${issue.issue_type}`,
                severity: issue.severity?.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
                category: issue.issue_type || 'unknown',
                title: issue.description || 'Data quality issue',
                description: issue.description || '',
                business_impact: issue.recommendation || '',
                technical_analysis: {
                    affected_columns: [col.column_name],
                }
            }))
        ) || [];

        return {
            "Executive Summary": [
                asyncResult.executive_summary?.dataset_overview || '',
                asyncResult.executive_summary?.data_completeness || '',
                asyncResult.executive_summary?.data_reliability || '',
                ...(asyncResult.executive_summary?.most_important_issues || []),
                ...(asyncResult.executive_summary?.next_steps || [])
            ].filter(Boolean),
            "Health Score": {
                "Data Quality Assessment (6 Dimensions)": asyncResult.health_score?.data_quality_assessment_6_dimensions?.toString() || '0',
                completeness: {
                    score: asyncResult.health_score?.completeness?.score?.toString() || '0',
                    details: asyncResult.health_score?.completeness?.evidence || ''
                },
                accuracy: {
                    score: asyncResult.health_score?.accuracy?.score?.toString() || '0',
                    details: asyncResult.health_score?.accuracy?.evidence || ''
                },
                consistency: {
                    score: asyncResult.health_score?.consistency?.score?.toString() || '0',
                    details: asyncResult.health_score?.consistency?.evidence || ''
                },
                validity: {
                    score: asyncResult.health_score?.validity?.score?.toString() || '0',
                    details: asyncResult.health_score?.validity?.evidence || ''
                },
                uniqueness: {
                    score: asyncResult.health_score?.uniqueness?.score?.toString() || '0',
                    details: asyncResult.health_score?.uniqueness?.evidence || ''
                },
                integrity: {
                    score: asyncResult.health_score?.integrity?.score?.toString() || '0',
                    details: asyncResult.health_score?.integrity?.evidence || ''
                }
            },
            "Detailed Problems": problems,
            analysis_metadata: {
                analysis_timestamp: asyncResult.execution_timestamp || new Date().toISOString(),
                analysis_method: 'async',
                config_applied: { metadata: asyncResult.metadata_used }
            },
            // Include DQ framework results if available from backend
            dq_framework_results: asyncResult.dq_framework_results
        };
    }

    /**
     * Legacy method - kept for backward compatibility, now uses async API internally
     * @deprecated Use startAsyncAnalysisTable and getReportFromAsyncJob instead
     */
    async analyzeDeltaTable(
        tableFullName: string,
        datasetName?: string,
        description?: string,
        options: ProfilerRequestOptions = {},
        dataObjectUuid?: string
    ): Promise<ProfilerAnalyzeResponse> {
        if (!tableFullName) {
            throw new Error('tableFullName is required (catalog.schema.table)');
        }

        if (!USE_BACKEND) {
            const mockReport = await this.getReport('mock');
            return this.transformMockToProfilerResponse(mockReport);
        }

        // Build FormData similar to file analysis, omitting file and adding table_full_name
        const formData = new FormData();
        formData.append('table_full_name', tableFullName);
        formData.append('dataset_name', datasetName || tableFullName);
        formData.append('description', description || '');

        const userEmail = authManager.getUserEmail();
        if (userEmail) {
            formData.append('user_email', userEmail);
        }
        if (dataObjectUuid) {
            formData.append('data_object_id', dataObjectUuid);
        }

        // Configuration flags (paralleling analyzeFile logic)
        formData.append('enable_reasoning', String(options.includeDetails ?? true));
        formData.append('enable_semantic_analysis', String(options.includeSamples ?? true));
        formData.append('enable_domain_validation', String(options.maxProblems ? options.maxProblems > 20 : true));



        // Endpoint chosen to parallel file diagnosis; adjust if backend specifies a different path
        const response = await httpRequest<ProfilerAnalyzeResponse>(
            apiPath('data-analysis/delta-table-diagnosis'),
            {
                method: 'POST',
                body: formData
            }
        );

        if (!response.success) {
            throw new Error((response as any).error?.message || 'Request failed');
        }

        const data = response.data;

        // Check if data is already in ProfilerAnalyzeResponse format (sync API) or raw async result
        if (data["Executive Summary"] && data["Health Score"] && data["Detailed Problems"]) {
            // Already in expected format (sync API response)
            if (dataObjectUuid) {
                this.storeAnalysisResults(dataObjectUuid, data);
                if (import.meta.env.DEV) {

                }
            }
            return data;
        }

        // If we reach here, the data format is not recognized - just return it as is
        if (dataObjectUuid) {
            this.storeAnalysisResults(dataObjectUuid, data);
        }
        return data;
    }

    /**
     * @deprecated Use startAsyncAnalysisTable() instead. This sync method should not be used.
     * Analyze a Databricks Delta table using the same data-diagnosis endpoint as file uploads
     * This method calls /api/v1/data-analysis/data-diagnosis with table_name instead of file
     * Based on the curl command provided for unified API usage
     */
    async analyzeDeltaTableUnified(
        tableFullName: string,
        datasetName?: string,
        description?: string,
        options: ProfilerRequestOptions = {},
        dataObjectUuid?: string
    ): Promise<ProfilerAnalyzeResponse> {
        if (!tableFullName) {
            throw new Error('tableFullName is required (catalog.schema.table)');
        }

        if (!USE_BACKEND) {
            const mockReport = await this.getReport('mock');
            return this.transformMockToProfilerResponse(mockReport);
        }

        // Build FormData to match the exact curl command format
        const formData = new FormData();

        // Required fields from curl command (in exact order)
        const userEmail = authManager.getUserEmail();
        if (userEmail) {
            formData.append('user_email', userEmail);
        }

        formData.append('table_name', tableFullName);
        formData.append('dataset_name', datasetName || tableFullName);

        // Add data_object_id if provided
        formData.append('data_object_id', dataObjectUuid || '');

        // Add structured description to metadata field (not description field)
        if (description) {
            formData.append('metadata', description);
        } else {
            formData.append('metadata', '');
        }
        // Do NOT add file field for Databricks tables - causes validation error

        // Do NOT add description field - not in curl command
        // Do NOT add enable_* fields - not in curl command



        // Use the same data-diagnosis endpoint as file uploads
        const response = await httpRequest<ProfilerAnalyzeResponse>(
            apiPath('data-analysis/data-diagnosis'),
            {
                method: 'POST',
                body: formData
            }
        );

        if (!response.success) {
            throw new Error((response as any).error?.message || 'Request failed');
        }

        const data = response.data;

        // Check for the actual API response structure
        const responseData = data as any; // Cast to any to handle new API structure

        if (!(responseData as any).summary && !(responseData as any).reasoning_result && !(responseData as any).metrics && !(responseData as any).executive_summary && !(responseData as any).health_score) {
            // If neither old nor new format, check for required fields in old format
            if (!data["Executive Summary"] || !data["Health Score"] || !data["Detailed Problems"]) {
                console.error('[ReportService] Invalid API response structure:', data);
                throw new Error('Invalid profiler response: missing required fields');
            }
        }

        // Transform new API format to expected format if needed
        if ((responseData as any).summary || (responseData as any).reasoning_result || (responseData as any).metrics || (responseData as any).executive_summary || (responseData as any).health_score) {
            // Backend returned new format, transform to expected format
            const executiveSummary: string[] = [];
            if ((responseData as any).executive_summary) {
                // Extract key insights from executive summary
                if ((responseData as any).executive_summary.dataset_overview) {
                    executiveSummary.push(`Dataset Overview: ${(responseData as any).executive_summary.dataset_overview}`);
                }
                if ((responseData as any).executive_summary.most_important_issues) {
                    executiveSummary.push(`Key Issues: ${(responseData as any).executive_summary.most_important_issues}`);
                }
                if ((responseData as any).executive_summary.next_steps) {
                    executiveSummary.push(`Next Steps: ${(responseData as any).executive_summary.next_steps}`);
                }
            }
            if (executiveSummary.length === 0) {
                executiveSummary.push("Analysis completed successfully");
            }

            // Transform health score structure
            const healthScore: any = {};
            if ((responseData as any).health_score) {
                // Copy all dimension scores
                Object.keys((responseData as any).health_score).forEach(key => {
                    if (key !== 'data_quality_assessment_6_dimensions' && key !== 'data_object_description') {
                        const dimension = (responseData as any).health_score[key];
                        if (dimension && typeof dimension === 'object' && dimension.score) {
                            // Convert percentage string to number
                            const score = parseFloat(dimension.score.replace('%', ''));
                            healthScore[key.charAt(0).toUpperCase() + key.slice(1)] = score;
                        }
                    }
                });

                // Set the overall score
                if ((responseData as any).health_score.data_quality_assessment_6_dimensions) {
                    const overallScore = parseFloat((responseData as any).health_score.data_quality_assessment_6_dimensions.replace('%', ''));
                    healthScore["Data Quality Assessment (6 Dimensions)"] = overallScore;
                    healthScore["overall"] = overallScore;
                }

                // Add dataset information from data_object_description
                if ((responseData as any).health_score.data_object_description) {
                    const desc = (responseData as any).health_score.data_object_description;
                    healthScore["Dataset Info"] = {
                        name: desc.dataset_name || 'Unknown Dataset',
                        description: desc.description || '',
                        totalRows: desc.total_rows || 0,
                        totalColumns: desc.total_columns || 0,
                        tableType: desc.table_type || 'Unknown',
                        totalProblems: desc.total_diagnosis_problems || 0
                    };
                }
            }

            // Transform detailed problems from reasoning result
            const detailedProblems: any[] = [];
            if ((responseData as any).reasoning_result?.problematic_columns) {
                (responseData as any).reasoning_result.problematic_columns.forEach((column: any) => {
                    column.issues?.forEach((issue: any) => {
                        detailedProblems.push({
                            category: issue.issue_type || 'Data Quality',
                            severity: issue.severity?.toLowerCase() || 'medium',
                            title: issue.brief_description || 'Data quality issue detected',
                            description: issue.description || issue.brief_description || 'No description provided',
                            impact: issue.business_impact || 'Potential impact on data reliability',
                            column_name: column.column_name,
                            criticality: issue.business_criticality || column.business_criticality || 'MEDIUM',
                            confidence: issue.confidence || 'MEDIUM',
                            remediation: issue.remediation ? {
                                solution_strategy: issue.remediation.solution_strategy || '',
                                action_plan: issue.remediation.action_plan || []
                            } : null,
                            root_cause: issue.root_cause_analysis || 'Not specified'
                        });
                    });
                });
            }

            const transformedData = {
                "Executive Summary": executiveSummary,
                "Health Score": healthScore,
                "Detailed Problems": detailedProblems
            } as any;

            if (import.meta.env.DEV) {

            }

            // Store transformed data
            if (dataObjectUuid) {
                this.storeAnalysisResults(dataObjectUuid, transformedData);

                // Update data object status to 'ready' after successful analysis
                try {
                    const user_email = authManager.getUserEmail();

                    // Use the already imported modules instead of dynamic imports

                    const queryParams = new URLSearchParams();
                    queryParams.append('user_email', user_email);
                    const endpoint = apiPath(`data-objects/${dataObjectUuid}?${queryParams.toString()}`);

                    await httpRequest(endpoint, {
                        method: 'PATCH',
                        body: JSON.stringify({ status: 'ready' }),
                        headers: { 'Content-Type': 'application/json' }
                    });

                } catch (statusUpdateError) {
                    console.error('[ReportService] Failed to update data object status to ready after unified delta table analysis:', {
                        dataObjectUuid,
                        error: statusUpdateError,
                        errorMessage: statusUpdateError instanceof Error ? statusUpdateError.message : 'Unknown error'
                    });
                    // Don't fail the whole operation if status update fails
                }
            }

            return transformedData;
        } else {
            // Original format, store and return as-is
            if (dataObjectUuid) {
                this.storeAnalysisResults(dataObjectUuid, data);

                // Update data object status to 'ready' after successful analysis
                try {
                    const user_email = authManager.getUserEmail();

                    // Use the already imported modules instead of dynamic imports

                    const queryParams = new URLSearchParams();
                    queryParams.append('user_email', user_email);
                    const endpoint = apiPath(`data-objects/${dataObjectUuid}?${queryParams.toString()}`);

                    await httpRequest(endpoint, {
                        method: 'PATCH',
                        body: JSON.stringify({ status: 'ready' }),
                        headers: { 'Content-Type': 'application/json' }
                    });

                } catch (statusUpdateError) {
                    console.error('[ReportService] Failed to update data object status to ready after unified delta table analysis (original format):', {
                        dataObjectUuid,
                        error: statusUpdateError,
                        errorMessage: statusUpdateError instanceof Error ? statusUpdateError.message : 'Unknown error'
                    });
                    // Don't fail the whole operation if status update fails
                }
            }

            return data;
        }
    }

    /**
     * Transform mock report data to profiler response format
     * Used when USE_BACKEND is false to maintain compatibility
     */
    private transformMockToProfilerResponse(
        mockReport: RawReportPayload
    ): ProfilerAnalyzeResponse {
        const now = new Date().toISOString();

        // Extract health scores from mock data
        const healthScore = mockReport['Health Score'];
        const overallScoreValue = healthScore['Data Quality Assessment (6 Dimensions)'];
        const overallScore = parseFloat(
            (typeof overallScoreValue === 'string' ? overallScoreValue : overallScoreValue?.score || '84.3')
                .replace('%', '')
        );

        return {
            "Executive Summary": mockReport["Executive Summary"] || [
                "Overall Data Quality Assessment: The dataset demonstrates good quality metrics with room for improvement in specific areas.",
                "Key Completeness Findings: Most columns show adequate data coverage with minimal missing values.",
                "Data Reliability for Business Use: The dataset is suitable for most analytical purposes.",
                "Critical Issues Requiring Attention: Some data standardization improvements needed.",
                "Overall Recommendation: Proceed with analysis while addressing identified issues."
            ],
            "Health Score": {
                "Data Quality Assessment (6 Dimensions)": `${overallScore}%`,
                completeness: {
                    score: `${parseFloat(
                        (typeof healthScore['Completeness'] === 'string'
                            ? healthScore['Completeness']
                            : healthScore['Completeness']?.score || '72.9'
                        ).replace('%', '')
                    )}%`,
                    details: "Completeness assessment details"
                },
                accuracy: {
                    score: `${parseFloat(
                        (typeof healthScore['Accuracy'] === 'string'
                            ? healthScore['Accuracy']
                            : healthScore['Accuracy']?.score || '72.9'
                        ).replace('%', '')
                    )}%`,
                    details: "Accuracy assessment details"
                },
                consistency: {
                    score: `${parseFloat(
                        (typeof healthScore['Consistency'] === 'string'
                            ? healthScore['Consistency']
                            : healthScore['Consistency']?.score || '83.9'
                        ).replace('%', '')
                    )}%`,
                    details: "Consistency assessment details"
                },
                validity: {
                    score: `${parseFloat(
                        (typeof healthScore['Validity'] === 'string'
                            ? healthScore['Validity']
                            : healthScore['Validity']?.score || '89.3'
                        ).replace('%', '')
                    )}%`,
                    details: "Validity assessment details"
                },
                uniqueness: {
                    score: `${parseFloat(
                        (typeof healthScore['Uniqueness'] === 'string'
                            ? healthScore['Uniqueness']
                            : healthScore['Uniqueness']?.score || '100.0'
                        ).replace('%', '')
                    )}%`,
                    details: "Uniqueness assessment details"
                },
                integrity: {
                    score: `${parseFloat(
                        (typeof healthScore['Integrity'] === 'string'
                            ? healthScore['Integrity']
                            : healthScore['Integrity']?.score || '94.0'
                        ).replace('%', '')
                    )}%`,
                    details: "Integrity assessment details"
                }
            },
            "Detailed Problems": mockReport['Detailed Problems'].map((problem, index) => ({
                id: `problem-${index + 1}`,
                severity: this.inferSeverityFromIssue(problem.Issue),
                category: this.extractCategoryFromIssue(problem.Issue),
                title: problem.Issue,
                description: problem.Problem_Details || problem.Issue,
                business_impact: problem.Business_Impact || 'Impact assessment needed',
                technical_analysis: {
                    affected_columns: this.extractColumnsFromIssue(problem.Issue),
                    affected_row_percentage: this.extractPercentageFromText(problem.Problem_Details),
                    missing_stats: problem.Technical_Analysis ? {
                        count: problem.Technical_Analysis.Missing_Count || 0,
                        percentage: parseFloat(problem.Technical_Analysis.Missing_Percentage?.replace('%', '') || '0')
                    } : undefined,
                    sample_values: {
                        valid_examples: problem.Reference_Valid_Examples || problem.Technical_Analysis?.Sample_Valid_Values,
                        current_values: problem.Technical_Analysis?.Sample_Valid_Values
                    }
                },
                solution_strategy: problem.Solution_Strategy,
                action_plan: problem.Action_Plan,
                imputation_guidance: problem.Imputation_Guidance,
                dimensions_affected: ['completeness'], // Default for mock
                confidence: 'high',
                detected_at: now
            })),
            analysis_metadata: {
                analysis_timestamp: now,
                analysis_method: 'Data Doctor Mock Profiler Service'
            }
        };
    }

    /**
     * Helper methods for mock data transformation
     */
    private inferSeverityFromIssue(issue?: string): 'low' | 'medium' | 'high' | 'critical' {
        if (!issue) return 'low';
        const lowerIssue = issue.toLowerCase();
        if (lowerIssue.includes('critical')) return 'critical';
        if (lowerIssue.includes('significant')) return 'high';
        if (lowerIssue.includes('moderate')) return 'medium';
        return 'low';
    }

    private extractCategoryFromIssue(issue?: string): string {
        if (!issue) return 'data_quality';
        const lowerIssue = issue.toLowerCase();
        if (lowerIssue.includes('completeness')) return 'completeness';
        if (lowerIssue.includes('consistency')) return 'consistency';
        if (lowerIssue.includes('validity')) return 'validity';
        return 'data_quality';
    }

    private extractColumnsFromIssue(issue?: string): string[] {
        // Extract column names from single quotes
        if (!issue) return [];
        const matches = issue.match(/'([^']+)'/g);
        return matches ? matches.map(match => match.replace(/'/g, '')) : [];
    }

    private extractPercentageFromText(text?: string): number | undefined {
        if (!text) return undefined;
        const match = text.match(/(\d+\.?\d*)%/);
        return match ? parseFloat(match[1]) : undefined;
    }
}

export const reportService = new ReportService();